import { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn';

interface TabItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
}

interface TabMenuProps {
  tabs: TabItem[];
  basePath: string;
  className?: string;
}

const tabVariants = cva(
  'cr-flex cr-flex-1 cr-items-center cr-justify-center cr-gap-x-2 cr-rounded-md cr-px-4 cr-py-2.5 cr-text-base cr-font-semibold cr-font-main cr-no-underline cr-shadow-md cr-transition-colors cr-duration-200 sm:cr-w-auto sm:cr-flex-initial',
  {
    variants: {
      active: {
        true: 'cr-bg-blue-900 cr-text-white',
        false: 'cr-bg-cirrusBlue-100 cr-text-blue-900 hover:cr-bg-gray-350',
      },
    },
    defaultVariants: {
      active: false,
    },
  }
);

export { tabVariants };

export default function TabMenu({ tabs, basePath, className = '' }: TabMenuProps): ReactElement {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className={cn('cr-w-full', className)}>
      <nav className="cr-flex cr-flex-wrap cr-gap-2" aria-label="Tabs">
        {tabs.map((tab) => {
          const tabPath = `${basePath}/${tab.path}`;
          const isActive = currentPath === tabPath || (tab.path === '' && currentPath === basePath);

          return (
            <Link
              key={tab.path}
              to={tabPath}
              className={tabVariants({ active: isActive })}
            >
              {tab.label}
              {tab.icon && <span className="cr-m-0">{tab.icon}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
